import {useTheme, useThemeProps} from "@mui/joy";
import {faArrowRightFromLine, faChevronRight, faPersonWalking, faTruck} from "@fortawesome/pro-regular-svg-icons";
import {useContext} from "react";
import {useSelf} from "../agent/useSelf.ts";
import {useMountedTruck} from "./mountedTruck.ts";
import {FontAwesomeIcon, FontAwesomeIconProps} from "@fortawesome/react-fontawesome";
import {SizeProp} from "@fortawesome/fontawesome-svg-core";
import {ActionButton, ActionButtonProps} from "../components/ActionButton.tsx";
import {useNavigate} from "@tanstack/react-router";
import {useTranslation} from "react-i18next";
import {useDismountTruckMutation, useMountTruckMutation} from "../tq/tqx.tsx";
import {keyify} from "../util/arutil.tsx";
import {ConfirmModalContext, ConfirmModalProps} from "../confirmModalProps.tsx";

export interface TruckMountButtonProps extends Omit<ActionButtonProps, "children" | "perform"> {
    truckId?: string;
    iconSize?: SizeProp;
}

// export const TruckMountButtonRoot
//     = styled("div", {
//     name: "TruckMountButton",
//     slot: "root"
// })<{ ownerState: TruckMountButtonProps }>({});

const mountIcons = keyify<Omit<FontAwesomeIconProps, "color" | "size">>([
    {icon: faPersonWalking},
    {icon: faChevronRight},
    {icon: faTruck},
]);

const dismountIcons = keyify<Omit<FontAwesomeIconProps, "color" | "size">>([
    {icon: faTruck},
    {icon: faArrowRightFromLine},
    {icon: faPersonWalking},
]);

export function TruckMountButton(inProps: TruckMountButtonProps) {
    const props = useThemeProps(
        {props: inProps, name: "TruckMountButton"});
    const theme = useTheme();
    const {truckId, iconSize: inIconSize, ...rest} = props;
    const iconSize = inIconSize ?? "2x";
    const selfTqrv = useSelf();
    const self = selfTqrv.data;
    const mountedTruckTqrv = useMountedTruck();
    const mountedTruck = mountedTruckTqrv.data;

    const {t} = useTranslation();
    const navigate = useNavigate();

    const {mutateAsync: performMount} = useMountTruckMutation()
    const {mutateAsync: performDismount} = useDismountTruckMutation();
    const confirmModalContext = useContext(ConfirmModalContext);

    if (!selfTqrv.isSuccess || !mountedTruckTqrv.isSuccess) {
        return null;
    }

    const canMount = self?.canDrive && !mountedTruck;
    const canDismount = mountedTruck && mountedTruck.id === truckId;

    if (!truckId || (!canMount && !canDismount)) {
        return null;
    }

    if (canMount) {
        const confirmModalProps: ConfirmModalProps = {
            question: t("driver.reallyMountQuestion"),
            yesAnswer: t("driver.reallyMountYes"),
            noAnswer: t("driver.reallyMountNo"),
            onYes: () => {
                void performMount({truckId});
            },
        };
        return <ActionButton variant="soft" {...rest} perform={() => {
            confirmModalContext.open(confirmModalProps);
            return Promise.resolve();
        }}>
            {mountIcons.map(kip => {
                return <FontAwesomeIcon {...kip.elem} key={kip.key}
                                        color={theme.palette.primary.softColor}
                                        size={iconSize}/>
            })}
        </ActionButton>
    }

    if (canDismount) {
        const confirmModalProps: ConfirmModalProps = {
            question: t("driver.reallyDismountQuestion", {truck: mountedTruck.displayName}),
            yesAnswer: t("driver.reallyDismountYes"),
            noAnswer: t("driver.reallyDismountNo"),
            onYes: () => {
                void performDismount()
                    .then(() => navigate({to: "/driver/schedule", params: {}}));
            },
        };
        return <ActionButton variant="soft" {...rest} perform={() => {
            confirmModalContext.open(confirmModalProps);
            return Promise.resolve();
        }}>
            {
                dismountIcons.map(kip => {
                    return <FontAwesomeIcon key={kip.key} {...kip.elem}
                                            color={theme.palette.primary.softColor}
                                            size={iconSize}/>
                })
            }
        </ActionButton>;
    }
}
